/**
 * Native-only app shell behaviors. No-op on web (gated on Capacitor.isNativePlatform()).
 *
 * 1. Android hardware back button: navigate back within history; exit app only at
 *    a root route (/member, /trainer, /admin, /login) or when there's nowhere to go back to.
 * 2. Network status tracking: renders a small fixed top banner when offline (native only).
 */
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Capacitor } from '@capacitor/core';

// Routes from which the back button should exit the app rather than navigate back.
const ROOT_ROUTES = ['/member', '/trainer', '/admin', '/login'];

function isRootRoute(pathname) {
  // Exact match or trailing-slash-only match (e.g. "/member" or "/member/").
  return ROOT_ROUTES.some((r) => pathname === r || pathname === `${r}/`);
}

export default function NativeAppHandler() {
  const { t } = useTranslation('common');
  const location = useLocation();
  const [isOffline, setIsOffline] = useState(false);

  // Keep the latest pathname available to the back-button listener without
  // re-registering the listener on every navigation.
  const pathnameRef = useRef(location.pathname);
  useEffect(() => {
    pathnameRef.current = location.pathname;
  }, [location.pathname]);

  // Android hardware back button.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let handle;
    let cancelled = false;

    (async () => {
      try {
        const { App } = await import('@capacitor/app');
        const listener = await App.addListener('backButton', ({ canGoBack }) => {
          const atRoot = isRootRoute(pathnameRef.current);
          const hasHistory =
            (canGoBack !== undefined ? canGoBack : window.history.length > 1);

          if (hasHistory && !atRoot) {
            window.history.back();
          } else {
            App.exitApp();
          }
        });
        if (cancelled) {
          listener.remove();
        } else {
          handle = listener;
        }
      } catch {}
    })();

    return () => {
      cancelled = true;
      if (handle) {
        try {
          handle.remove();
        } catch {}
      }
    };
  }, []);

  // Network status.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let handle;
    let cancelled = false;

    (async () => {
      try {
        const { Network } = await import('@capacitor/network');
        const status = await Network.getStatus();
        if (!cancelled) setIsOffline(!status.connected);

        const listener = await Network.addListener('networkStatusChange', (s) => {
          setIsOffline(!s.connected);
        });
        if (cancelled) {
          listener.remove();
        } else {
          handle = listener;
        }
      } catch {}
    })();

    return () => {
      cancelled = true;
      if (handle) {
        try {
          handle.remove();
        } catch {}
      }
    };
  }, []);

  if (!Capacitor.isNativePlatform() || !isOffline) return null;

  return (
    <div className="fixed top-0 inset-x-0 z-[9999] bg-red-600 text-white text-center text-sm py-1.5 px-3 shadow-md">
      {t('offline', 'Nema internetske veze')}
    </div>
  );
}
